import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ReactComponent as InfoTooltipIcon } from '@assets/images/Input/info-tooltip.svg'
import type { ComboboxOption, ComboboxProps } from './Combobox.types'
import {
  COMBOBOX_DEMO_ERROR_MESSAGE,
  COMBOBOX_DEMO_FILLED_VALUE,
  COMBOBOX_DEMO_INFO_MESSAGE,
  COMBOBOX_DEMO_LABEL,
  COMBOBOX_DEMO_OPTIONS,
  COMBOBOX_DEMO_PLACEHOLDER,
} from './Combobox.types'
import './Combobox.css'

const filterOptions = (
  options: ComboboxOption[],
  query: string,
  filterable: boolean,
): ComboboxOption[] => {
  if (!filterable || !query.trim()) {
    return options
  }

  const normalizedQuery = query.trim().toLowerCase()

  return options.filter((option) =>
    option.label.toLowerCase().includes(normalizedQuery),
  )
}

const Combobox: React.FC<ComboboxProps> = ({
  label = COMBOBOX_DEMO_LABEL,
  placeholder = COMBOBOX_DEMO_PLACEHOLDER,
  options = COMBOBOX_DEMO_OPTIONS,
  value,
  defaultValue = '',
  allowCustom = true,
  filterable = true,
  disabled = false,
  error = false,
  helperText,
  optional = false,
  showTooltip = false,
  className = '',
  darkMode = false,
  previewState,
  onChange,
}) => {
  const inputId = useId()
  const listboxId = useId()
  const fieldRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const isPreview = previewState != null
  const isDisabled = disabled || previewState === 'disabled'
  const isError = error || previewState === 'error'
  const isOptional = optional || previewState === 'optional'
  const hasTooltip = showTooltip || previewState === 'with-tooltip'

  const resolvedValue = useMemo(() => {
    if (isPreview) {
      if (
        previewState === 'filled' ||
        previewState === 'disabled' ||
        previewState === 'error' ||
        previewState === 'with-info' ||
        previewState === 'expanded'
      ) {
        return COMBOBOX_DEMO_FILLED_VALUE
      }

      return ''
    }

    return value ?? internalValue
  }, [internalValue, isPreview, previewState, value])

  const resolvedHelperText = useMemo(() => {
    if (helperText != null) {
      return helperText
    }

    if (previewState === 'error') {
      return COMBOBOX_DEMO_ERROR_MESSAGE
    }

    if (previewState === 'with-info') {
      return COMBOBOX_DEMO_INFO_MESSAGE
    }

    return undefined
  }, [helperText, previewState])

  const filteredOptions = useMemo(
    () => filterOptions(options, resolvedValue, filterable && !isPreview),
    [filterable, isPreview, options, resolvedValue],
  )

  const displayOptions = isPreview ? options : filteredOptions

  const isMenuVisible =
    isPreview && previewState === 'expanded' ? true : menuOpen

  const isOpenTrigger =
    isMenuVisible && (previewState === 'expanded' || (!isPreview && menuOpen))

  const emitChange = useCallback(
    (nextValue: string) => {
      if (value === undefined) {
        setInternalValue(nextValue)
      }

      onChange?.(nextValue)
    },
    [onChange, value],
  )

  const openMenu = useCallback(() => {
    if (isPreview || isDisabled) {
      return
    }

    setMenuOpen(true)
  }, [isDisabled, isPreview])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
    setActiveIndex(-1)
  }, [])

  const handleToggleMenu = useCallback(() => {
    if (isPreview || isDisabled) {
      return
    }

    setMenuOpen((open) => {
      if (open) {
        setActiveIndex(-1)
        return false
      }

      return true
    })
    inputRef.current?.focus()
  }, [isDisabled, isPreview])

  const handleSelectOption = useCallback(
    (optionValue: string) => {
      emitChange(optionValue)
      closeMenu()
      inputRef.current?.focus()
    },
    [closeMenu, emitChange],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isPreview || isDisabled) {
        return
      }

      const nextValue = event.target.value
      emitChange(nextValue)
      setMenuOpen(true)
      setActiveIndex(-1)
    },
    [emitChange, isDisabled, isPreview],
  )

  const handleInputBlur = useCallback(() => {
    if (isPreview || isDisabled || allowCustom) {
      return
    }

    const exactMatch = options.find(
      (option) =>
        option.label.toLowerCase() === resolvedValue.trim().toLowerCase() ||
        option.value.toLowerCase() === resolvedValue.trim().toLowerCase(),
    )

    if (!exactMatch && resolvedValue) {
      emitChange('')
    }
  }, [allowCustom, emitChange, isDisabled, isPreview, options, resolvedValue])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isPreview || isDisabled) {
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!menuOpen) {
          setMenuOpen(true)
          setActiveIndex(0)
          return
        }

        setActiveIndex((index) =>
          displayOptions.length === 0
            ? -1
            : Math.min(index + 1, displayOptions.length - 1),
        )
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (!menuOpen) {
          setMenuOpen(true)
          setActiveIndex(displayOptions.length - 1)
          return
        }

        setActiveIndex((index) => Math.max(index - 1, 0))
        return
      }

      if (event.key === 'Enter' && menuOpen && activeIndex >= 0) {
        event.preventDefault()
        const option = displayOptions[activeIndex]
        if (option) {
          handleSelectOption(option.value)
        }
        return
      }

      if (event.key === 'Escape' && menuOpen) {
        event.preventDefault()
        closeMenu()
      }
    },
    [
      activeIndex,
      closeMenu,
      displayOptions,
      handleSelectOption,
      isDisabled,
      isPreview,
      menuOpen,
    ],
  )

  useEffect(() => {
    if (isPreview || !menuOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (
        fieldRef.current &&
        !fieldRef.current.contains(event.target as Node)
      ) {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [closeMenu, isPreview, menuOpen])

  const classNames = [
    'ds-combobox',
    isDisabled ? 'ds-combobox--disabled' : '',
    isError ? 'ds-combobox--error' : '',
    darkMode ? 'ds-combobox--dark' : '',
    isPreview ? 'ds-combobox--preview' : '',
    previewState ? `ds-combobox--preview-${previewState}` : '',
    resolvedValue ? 'ds-combobox--filled' : '',
    isOpenTrigger ? 'ds-combobox--open' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classNames}>
      <div className="ds-combobox__label-row">
        <label className="ds-combobox__label" htmlFor={inputId}>
          {label}
          {isOptional ? (
            <span className="ds-combobox__optional"> (Optional)</span>
          ) : null}
        </label>
        {hasTooltip ? (
          <span
            className="ds-combobox__tooltip-trigger"
            title={label}
            aria-label={`More information about ${label}`}
          >
            <InfoTooltipIcon />
          </span>
        ) : null}
      </div>

      <div
        className={`ds-combobox__field${
          previewState === 'focused'
            ? ' ds-combobox__field--focused-preview'
            : ''
        }`}
        ref={fieldRef}
      >
        <div className="ds-combobox__control">
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            className="ds-combobox__input"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isMenuVisible}
            aria-controls={listboxId}
            aria-activedescendant={
              activeIndex >= 0
                ? `${listboxId}-option-${activeIndex}`
                : undefined
            }
            placeholder={placeholder}
            value={resolvedValue}
            disabled={isPreview || isDisabled}
            tabIndex={isPreview ? -1 : 0}
            autoComplete="off"
            onChange={handleInputChange}
            onFocus={openMenu}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            className="ds-combobox__toggle"
            tabIndex={isPreview ? -1 : 0}
            aria-label={isMenuVisible ? 'Close options' : 'Open options'}
            aria-expanded={isMenuVisible}
            aria-controls={listboxId}
            disabled={isPreview || isDisabled}
            onMouseDown={(event) => {
              event.preventDefault()
            }}
            onClick={handleToggleMenu}
          >
            <i
              className="mgc mgc_down_small_fill ds-combobox__chevron"
              aria-hidden="true"
            />
          </button>
        </div>

        {isMenuVisible ? (
          <div id={listboxId} className="ds-combobox__menu" role="listbox">
            <div className="ds-combobox__menu-scroll">
              {displayOptions.length === 0 ? (
                <p className="ds-combobox__empty">No matching options</p>
              ) : (
                displayOptions.map((option, index) => {
                  const isSelected = resolvedValue === option.value
                  const isActive = index === activeIndex

                  return (
                    <button
                      key={option.value}
                      id={`${listboxId}-option-${index}`}
                      type="button"
                      className={`ds-combobox__option${
                        isSelected || isActive
                          ? ' ds-combobox__option--selected'
                          : ''
                      }${isActive ? ' ds-combobox__option--active' : ''}`}
                      role="option"
                      aria-selected={isSelected}
                      disabled={isPreview || isDisabled}
                      onMouseDown={(event) => {
                        event.preventDefault()
                      }}
                      onClick={() => handleSelectOption(option.value)}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      <span className="ds-combobox__option-label">
                        {option.label}
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        ) : null}
      </div>

      {resolvedHelperText ? (
        <p
          className={`ds-combobox__helper${
            isError ? ' ds-combobox__helper--error' : ''
          }`}
        >
          {resolvedHelperText}
        </p>
      ) : null}
    </div>
  )
}

export default Combobox
